import React, { useMemo, useState } from 'react';
import { Check, CircleSlash, Image as ImageIcon, RefreshCw, Search, Tag, TriangleAlert } from 'lucide-react';
import { Tooltip } from '../ui/Tooltip';
import { RegulationLink } from '../ui/RegulationLink';
type NipVerdict = 'Permitted' | 'OK — food ingredient' | 'Not in TGA table';
type NipIngredient = {
  name: string;
  resolved?: string;
  verdict: NipVerdict;
  amount?: string;
  limit?: string;
  source?: string;
};
type NipGroup = {
  title: string;
  ratio: string;
  ingredients: NipIngredient[];
};
const filters = ['All', 'Restricted', 'Prohibited', 'Food ingredient (OK) 3', 'Not in Code schedules', 'Not listable', 'Unknown'];
const completenessItems = ['servings per package', 'serving size', 'Energy', 'Protein', 'fat total', 'saturated fat', 'Carbohydrate', 'Sugars', 'Sodium'];
const groups: NipGroup[] = [{
  title: 'Additives',
  ratio: '5/5',
  ingredients: [{
    name: 'Vitamin B6, mg',
    resolved: 'pyridoxine hydrochloride',
    verdict: 'Permitted',
    amount: '— No limit',
    limit: 'No numeric limit defined.',
    source: 'Schedule 17 Vitamins and minerals'
  }, {
    name: 'Vitamin B12, µg',
    resolved: 'cyanocobalamin',
    verdict: 'Permitted',
    amount: '— No limit',
    limit: 'No numeric limit defined.',
    source: 'Schedule 17 Vitamins and minerals'
  }, {
    name: 'Vitamin C, mg',
    resolved: 'Ascorbic acid, L-',
    verdict: 'Permitted',
    amount: '— No limit',
    limit: 'No numeric limit defined.',
    source: 'Schedule 17 Vitamins and minerals'
  }, {
    name: 'Phosphorus, mg',
    resolved: 'PHOSPHORUS',
    verdict: 'OK — food ingredient'
  }, {
    name: 'Niacin (B3), mg',
    resolved: 'nicotinamide',
    verdict: 'Permitted',
    amount: '— No limit',
    limit: 'No numeric limit defined.',
    source: 'Schedule 17 Vitamins and minerals'
  }]
}, {
  title: 'Processing aids',
  ratio: '3/3',
  ingredients: [{
    name: 'Riboflavin (B2), mg',
    resolved: 'Riboflavins',
    verdict: 'Permitted',
    amount: '— No limit',
    limit: 'GMP — permitted at any level (Schedule 16)',
    source: 'FSANZ Schedule 15/16'
  }, {
    name: 'Calcium, mg',
    resolved: 'Calcium',
    verdict: 'OK — food ingredient'
  }, {
    name: 'Iron, mg',
    resolved: 'Iron',
    verdict: 'OK — food ingredient'
  }]
}, {
  title: 'Flavourings',
  ratio: '0/0',
  ingredients: []
}, {
  title: 'Nutrients',
  ratio: '0/0',
  ingredients: []
}, {
  title: 'Probiotics',
  ratio: '0',
  ingredients: []
}];
const nutritionRows: string[][] = [['Energy (kJ)', '340', '1690', '630', '7%', '—'], ['Energy (Cal)', '80', '400', '150', '—', '—'], ['Protein (g)', '2.5', '12.3', '10.0', '20%', '—'], ['Fat, Total (g)', '1.7', '8.4', '1.7', '2%', '—'], ['Saturated (g)', '1.0', '4.9', '1.0', '4%', '—'], ['Carbohydrate (g)', '11.9', '59.5', '22.8', '7%', '—'], ['Sugars (g)', '8.8', '43.6', '19.8', '22%', '—'], ['Lactose (g)', '2.5', '12.3', '13.5', '—', '—'], ['Added Sugars (g)', '6.3', '31.3', '6.3', '—', '—'], ['Dietary Fibre (g)', '0.8', '4.1', '0.8', '3%', '—'], ['Sodium (mg)', '28', '140', '110', '5%', '—'], ['Vitamin D, µg', '2.7', '13.5', '2.8', '28%', '—']];
const nutritionHeaders = ['Nutrient', 'Per 20g', 'Per 100g', 'Per 20g + 200ml skim milk', '%DI†', '%RDI'];
export function NipTab() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [query, setQuery] = useState('');
  const [showLabelImage, setShowLabelImage] = useState(false);
  const visibleGroups = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return groups;
    return groups.map(group => ({
      ...group,
      ingredients: group.ingredients.filter(item => `${item.name} ${item.resolved ?? ''}`.toLowerCase().includes(term))
    })).filter(group => group.ingredients.length > 0);
  }, [query]);
  return <div className="space-y-5" data-id="element-993" data-mp="3fiujah23ouro">
      <section className="rounded-2xl border border-[#dce3da] bg-white shadow-sm" aria-labelledby="npsc-heading" data-id="element-994" data-mp="pshfrp0n547x">
        <div className="flex flex-col gap-3 border-b border-[#e5ebe3] px-6 py-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-995" data-mp="uqsn9t3gr2mz">
          <div data-id="element-996" data-mp="sdf4echbd7zz">
            <h2 id="npsc-heading" className="font-[Figtree] text-xl font-semibold text-evergreen" data-id="element-997" data-mp="scx9bj6e0ztn">NPSC score</h2>
            <p className="mt-1 text-sm text-[#718174]" data-id="element-998" data-mp="od4vos2503hf">Does not meet the NPSC.</p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-full bg-red-100 px-3 py-1.5 text-sm font-semibold text-red-800 sm:self-auto" data-id="element-999" data-mp="128qjwx0jqj8s">
            <CircleSlash className="h-4 w-4" data-id="element-1000" data-mp="1ba6tx3ng8jch" />
            Exceeds NPSC threshold
          </span>
        </div>
        <div className="px-6 py-5" data-id="element-1001" data-mp="179ej45yxi6e3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1002" data-mp="1l1b7q01e1y5q">
            <MetricTile label="Category" value="General" icon={<Tag className="h-3.5 w-3.5" data-id="element-1004" data-mp="1g1q0l2c2ejhx" />} sub="Taxonomy match" data-id="element-1003" data-mp="1l0jakks57sdc" />
            <MetricTile label="Final score" value="15" sub="≤ 4" data-id="element-1005" data-mp="1g4kkmsr622at" />
            <MetricTile label="Baseline" value="19" sub="Energy + sat fat + sugars + sodium" data-id="element-1006" data-mp="1v00fgzzkm289" />
            <MetricTile label="V / P / F" value="0 · 0 · 4" data-id="element-1007" data-mp="1q1yyqlpmaezy" />
          </div>
          <p className="mt-4 inline-flex items-center gap-2 text-sm text-[#56685c]" data-id="element-1008" data-mp="29lxr6d6gvlyx">
            <Check className="h-4 w-4 text-forest-green" data-id="element-1009" data-mp="2doaxb2p4llw8" />
            All baseline nutrients present per 100 g/mL.
          </p>
        </div>
      </section>

      <section className="rounded-2xl border border-[#dce3da] bg-white shadow-sm" aria-labelledby="nip-heading" data-id="element-1010" data-mp="1zq4788qf4q74">
        <div className="border-b border-[#e5ebe3] px-6 py-5" data-id="element-1011" data-mp="24oxl429maj7t">
          <div className="flex flex-wrap items-center gap-3" data-id="element-1012" data-mp="1pwng5cho1p75">
            <h2 id="nip-heading" className="font-[Figtree] text-xl font-semibold text-evergreen" data-id="element-1013" data-mp="1upglo2kyl1ka">Nutrition Information Panel</h2>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700" data-id="element-1014" data-mp="1gythvw0cclhc">
              <RefreshCw className="h-3.5 w-3.5" data-id="element-1015" data-mp="1lu8vqnycmhdr" />
              Standard NIP
            </span>
          </div>
          <p className="mt-1 text-sm text-[#718174]" data-id="element-1016" data-mp="1byfl5a46wxym">Values extracted from the nutrition label.</p>
          <div className="mt-4 flex flex-wrap gap-2" data-id="element-1017" data-mp="stu1egkq70le">
            <span className="rounded-full border border-[#d8e1d5] bg-[#fbfdf9] px-2.5 py-1 text-xs font-semibold text-[#56685c]" data-id="element-1018" data-mp="svdgw4yk57m4">Serving: 20g</span>
            <span className="rounded-full border border-[#d8e1d5] bg-[#fbfdf9] px-2.5 py-1 text-xs font-semibold text-[#56685c]" data-id="element-1019" data-mp="j3r9ykt95up6">Servings/package: 23</span>
          </div>
        </div>

        <div className="border-b border-[#e5ebe3] px-6 py-5" data-id="element-1020" data-mp="e33977f81zhr">
          <div className="flex flex-wrap items-center gap-3" data-id="element-1021" data-mp="1bs6ak2bn0agz">
            <h3 className="font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#708174]" data-id="element-1022" data-mp="16t243jg3kquc">NIP completeness</h3>
            <span className="rounded-full bg-forest-green/10 px-2.5 py-1 text-xs font-semibold text-forest-green" data-id="element-1023" data-mp="11tgzoti5rqa8">Complete</span>
          </div>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1024" data-mp="wxryklqhq1bt">
            {completenessItems.map(item => <li key={item} className="inline-flex items-center gap-2 text-sm text-[#56685c]" data-id="element-1025" data-mp="3hlwlzi06ekav">
                <Check className="h-4 w-4 shrink-0 text-forest-green" data-id="element-1026" data-mp="1x3vstczq71ev" />
                {item}
              </li>)}
          </ul>
        </div>

        <div className="px-6 py-5" data-id="element-1027" data-mp="222ymy2e63n82">
          <h3 className="font-mono text-xs font-semibold uppercase tracking-[0.15em] text-[#708174]" data-id="element-1028" data-mp="2721srp1z1xh3">Active ingredients from NIP</h3>

          <div className="mt-3 flex flex-col gap-3 rounded-xl border border-[#e1e8df] bg-[#fbfdf9] px-4 py-3" data-id="element-1029" data-mp="2btjs68ocjpzy">
            <span className="font-mono text-xs font-semibold uppercase tracking-[0.11em] text-[#6f8174]" data-id="element-1030" data-mp="2gqtirrnh27pd">5 permitted · 0 with conditions · 0 not permitted · 3 food · 0 verification required</span>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-1031" data-mp="2ptksnn0p8vew">
              <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter ingredients" data-id="element-1032" data-mp="2uq1jioza9nvo">
                {filters.map(filter => <button key={filter} type="button" onClick={() => setActiveFilter(filter)} aria-pressed={activeFilter === filter} className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition ${activeFilter === filter ? 'border-forest-green bg-forest-green text-white' : 'border-[#d8e1d5] bg-white text-[#56685c] hover:bg-[#f3f7f1]'}`} data-id="element-1033" data-mp="10dbna0lg02l4">
                    {filter}
                  </button>)}
              </div>
              <label className="relative shrink-0" data-id="element-1034" data-mp="2ed6dexfhko1f">
                <span className="sr-only" data-id="element-1035" data-mp="2eehid0ko41u4">Search ingredients</span>
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#93a095]" data-id="element-1036" data-mp="2tagxuwa3x49a" />
                <input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search ingredients" className="w-full rounded-lg border border-[#d8e1d5] bg-white py-2 pl-9 pr-3 text-sm text-evergreen placeholder:text-[#9aa79c] focus:border-forest-green focus:outline-none focus:ring-1 focus:ring-forest-green lg:w-64" data-id="element-1037" data-mp="2oab51cs3pbpm" />
              </label>
            </div>
          </div>

          <div className="mt-4 space-y-4" data-id="element-1038" data-mp="25j001h27ghe">
            {visibleGroups.map(group => <div key={group.title} data-id="element-1039" data-mp="65tfc4qqrj8x">
                <div className="flex items-center justify-between border-b border-[#e5ebe3] pb-2" data-id="element-1040" data-mp="tuvu94tzqxzq">
                  <h4 className="font-[Figtree] text-sm font-semibold text-evergreen" data-id="element-1041" data-mp="ytz0gslpja9a">{group.title}</h4>
                  <span className="font-mono text-xs text-[#8b968d]" data-id="element-1042" data-mp="l2ty3iqyg3v9">{group.ratio}</span>
                </div>
                {group.ingredients.length > 0 && <ul className="divide-y divide-[#edf0eb]" data-id="element-1043" data-mp="p2ck6huiikzo">
                    {group.ingredients.map(ingredient => <li key={`${group.title}-${ingredient.name}`} className="py-3" data-id="element-1044" data-mp="1hkgfxvhj49cp">
                        <div className="flex flex-wrap items-center gap-2" data-id="element-1045" data-mp="1hmkquxdui8mh">
                          <p className="text-sm font-semibold text-evergreen" data-id="element-1046" data-mp="35idfv1yrpoja">
                            {ingredient.name}
                            {ingredient.resolved && <span className="font-normal text-[#718174]" data-id="element-1047" data-mp="2vjwk7rovwlqw"> → {ingredient.resolved}</span>}
                          </p>
                          <NipVerdictBadge verdict={ingredient.verdict} data-id="element-1048" data-mp="30hfz8ttep2jw" />
                          {ingredient.amount && <span className="font-mono text-xs text-[#8b968d]" data-id="element-1049" data-mp="2mqb5g8cpz4hh">{ingredient.amount}</span>}
                        </div>
                        {ingredient.limit && <p className="mt-1 text-sm text-[#617366]" data-id="element-1050" data-mp="2ct6kucyivh2s">{ingredient.limit}</p>}
                        {ingredient.source && <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-[#8b968d]" data-id="element-1051" data-mp="2hrzwdzu90y0b">Source: <RegulationLink text={ingredient.source} data-id="element-1052" data-mp="23sgp1999hqp2" /></p>}
                      </li>)}
                  </ul>}
              </div>)}
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" aria-labelledby="nutrition-table-heading" data-id="element-1053" data-mp="1p6t4hmz5ck5o">
        <div className="flex flex-col gap-3 border-b border-[#e5ebe3] px-6 py-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-1054" data-mp="1k8ro59t3uyxw">
          <h2 id="nutrition-table-heading" className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-1055" data-mp="1t7mqubwxxqwg">Nutrition table</h2>
          <button type="button" onClick={() => setShowLabelImage(current => !current)} aria-expanded={showLabelImage} className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5] sm:self-auto" data-id="element-1056" data-mp="283lyjlg51p12">
            <ImageIcon className="h-4 w-4" data-id="element-1057" data-mp="2qulux7pyq14d" />
            {showLabelImage ? 'Hide label image' : 'Compare with label image'}
          </button>
        </div>

        {showLabelImage ? <div className="grid gap-4 px-6 py-5 lg:grid-cols-2" data-id="element-1058" data-mp="1jiw6i485pa72">
            <div className="max-h-[560px] overflow-y-auto" data-id="element-1059" data-mp="1jkh0nsqk6r4e">
              <p className="text-xs text-[#8b968d]" data-id="element-1060" data-mp="1njg0iyxfnxoz">Source: label artwork, face 2 of 3.</p>
              <div className="mt-2 overflow-hidden rounded-xl border border-[#e1e8df] bg-[#f6f8f5]" data-id="element-1061" data-mp="1sj2g3f0qhq0y">
                <img src="https://cdn.magicpatterns.com/uploads/rEknYbNMi51WbMMedg8Jbp/Chocolate_Drink_Nutri_Panel.png" alt="Nutrition information panel from the label artwork" className="mx-auto block aspect-[3/4] w-full object-contain" data-id="element-1062" data-mp="1xaaoq8u8p7d2" />
              </div>
            </div>
            <div className="max-h-[560px] overflow-auto rounded-xl border border-[#e1e8df]" data-id="element-1063" data-mp="2b7pkdvb36bdh">
              <NutritionTable compact data-id="element-1064" data-mp="2g83rnb4v5hag" />
            </div>
          </div> : <div className="overflow-x-auto" data-id="element-1065" data-mp="gxc4jfncusdv">
            <NutritionTable data-id="element-1066" data-mp="luvqz2u7ibgw" />
          </div>}
      </section>
    </div>;
}
function NutritionTable({
  compact = false
}: {
  compact?: boolean;
}) {
  const headers = compact ? ['Nutrient', 'Per 20g', 'Per 100g', '%DI†'] : nutritionHeaders;
  return <table className={`w-full text-left text-sm ${compact ? '' : 'min-w-[720px]'}`} data-id="element-1067" data-mp="9j1wcwgv17zx">
      <thead className="bg-[#fbfdf9] font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[#748477]" data-id="element-1068" data-mp="4mbga9522cei">
        <tr data-id="element-1069" data-mp="3vtnvwjqaihsb">
          {headers.map((header, index) => <th key={header} className={`${compact ? 'px-3' : 'px-5'} py-3 ${index === 0 ? '' : 'text-right'}`} data-id="element-1070" data-mp="xah26z7ui7fx" data-mpi={index}>{header}</th>)}
        </tr>
      </thead>
      <tbody className="divide-y divide-[#edf0eb]" data-id="element-1071" data-mp="s9b2uijojlxf">
        {nutritionRows.map(row => {
        const values = compact ? [row[1], row[2], row[4]] : row.slice(1);
        return <tr key={row[0]} data-id="element-1072" data-mp="jciqk1kwsp44">
              <td className={`${compact ? 'px-3' : 'px-5'} py-3 font-semibold text-evergreen`} data-id="element-1073" data-mp="382rxh3rbwoc3">
                {row[0]}
                {row[0] === 'Vitamin D, µg' && <span className="mt-0.5 block text-xs font-normal text-[#8b968d]" data-id="element-1074" data-mp="33zdi8fb7sbzp">Vitamins &amp; Minerals (%RDI)</span>}
              </td>
              {values.map((value, index) => {
            const isDiCell = compact && index === values.length - 1;
            return <td key={`${row[0]}-${index}`} className={`${compact ? 'px-3' : 'px-5'} py-3 text-right font-mono text-[#617366]`} data-id="element-1075" data-mp="lvdq0zbjhnm6" data-mpi={index}>
                    {isDiCell ? <Tooltip content={`Per 20g + 200ml skim milk: ${row[3]}`} data-id="element-1076" data-mp="1ei6thpgbd0zv">
                        <span className="cursor-help border-b border-dotted border-[#b6c0b7]" data-id="element-1077" data-mp="1jhb14bnlaqo3">{value}</span>
                      </Tooltip> : value}
                  </td>;
          })}
            </tr>;
      })}
      </tbody>
    </table>;
}
function MetricTile({
  label,
  value,
  sub,
  icon
}: {
  label: string;
  value: string;
  sub?: string;
  icon?: React.ReactNode;
}) {
  return <div className="rounded-xl border border-[#e1e8df] bg-[#fbfdf9] px-4 py-3" data-id="element-1078" data-mp="3u7hz4h3oj6sy">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#748477]" data-id="element-1079" data-mp="3moig4ck4rha4">{label}</p>
      <p className="mt-1.5 flex items-center gap-1.5 font-[Figtree] text-2xl font-semibold text-evergreen" data-id="element-1080" data-mp="3hqqlhas3ph28">
        {icon && <span className="text-[#718174]" data-id="element-1081" data-mp="gis100pp758">{icon}</span>}
        {value}
      </p>
      {sub && <p className="mt-1 text-xs text-[#8b968d]" data-id="element-1082" data-mp="3rn3f68nnbuwz">{sub}</p>}
    </div>;
}
function NipVerdictBadge({
  verdict
}: {
  verdict: NipVerdict;
}) {
  if (verdict === 'Not in TGA table') {
    return <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800" data-id="element-1083" data-mp="2yyedo8iohnkp">
        <TriangleAlert className="h-3.5 w-3.5" data-id="element-1084" data-mp="38yyi1k1hv875" />
        Not in TGA table
      </span>;
  }
  return <span className="inline-flex rounded-full bg-forest-green/10 px-2.5 py-1 text-xs font-semibold text-forest-green" data-id="element-1085" data-mp="2l8ugdi7v9iis">{verdict}</span>;
}